/** Fixture data for the admin portal. Replaced by real API calls once backend lands. */

import {
  accounts,
  children,
  childLinks,
  consents,
  memberships,
  seats as fixtureSeats,
  license,
} from "../../domain/fixtures";
import { createSeatState, seatSummary } from "../../domain/seats";
import type { SeatState } from "../../domain/seats";
import type { MembershipRole, SeatStatus } from "../../domain/types";

export interface PendingInvite {
  id: string;
  email: string;
  role: MembershipRole;
  invitedAt: string;
}

/** The one invite that shows up in the Overview activity log and pending-invite count. */
export const FIXTURE_PENDING_INVITES: PendingInvite[] = [
  {
    id: "inv-sarah",
    email: "sarah.chen@brightops.org",
    role: "member",
    invitedAt: "2026-06-17T09:00:00.000Z",
  },
];

export type ActivityKind = "member" | "seat" | "invite" | "org";

export interface ActivityItem {
  id: string;
  text: string;
  timeLabel: string;
  kind: ActivityKind;
}

// Derived from the single fixture source so every admin screen agrees (no
// hand-kept numbers that drift apart). Seats follow the same capacity math as
// the Seats & licence screen; children = unique linked children (not links).
const _seatSummary = seatSummary(createSeatState(license, fixtureSeats));

export const ADMIN_ORG = {
  name: "Bright Steps Centre",
  type: "Early childhood centre",
  seatsTotal: _seatSummary.seatsTotal,
  seatsUsed: _seatSummary.seatsUsed,
  memberCount: memberships.length,
  adminCount: memberships.filter((m) => m.role === "admin").length,
  linkedChildCount: children.length,
  pendingInviteCount: FIXTURE_PENDING_INVITES.length,
} as const;

export const ACTIVITY: ActivityItem[] = [
  { id: "a1", text: "Marcus Bell joined as admin", timeLabel: "Today · 09:30", kind: "member" },
  { id: "a2", text: "Seat assigned to Dr Priya Nair", timeLabel: "Today · 09:28", kind: "seat" },
  {
    id: "a3",
    text: "New member invited: sarah.chen@brightops.org",
    timeLabel: "Yesterday",
    kind: "invite",
  },
  { id: "a4", text: "Seat assigned to Marcus Bell", timeLabel: "14 Jun", kind: "seat" },
  { id: "a5", text: "Organisation created", timeLabel: "12 Jun", kind: "org" },
];

// ---- Members + seats (driven by domain fixtures) ----------------------------

export interface MemberRow {
  accountId: string;
  email: string;
  role: MembershipRole;
  hasSeat: boolean;
  seatStatus: SeatStatus | null;
}

/** Builds a member row for every entry in the memberships fixture. */
export function buildMemberRows(): MemberRow[] {
  return memberships.map((m) => {
    const account = accounts.find((a) => a.id === m.accountId);
    const seat = fixtureSeats.find((s) => s.accountId === m.accountId);
    return {
      accountId: m.accountId,
      email: account?.email ?? m.accountId,
      role: m.role,
      hasSeat: !!seat,
      seatStatus: seat?.status ?? null,
    };
  });
}

/** Initial seat ledger built from the licence + fixture seat rows. */
export function buildInitialSeatState(): SeatState {
  return createSeatState(license, fixtureSeats);
}

export const FIXTURE_LICENSE = license;
export type { SeatState };

// ---- Roster oversight -------------------------------------------------------

export type RosterSharingStatus = "active" | "pending" | "revoked";

export interface RosterRow {
  childId: string;
  displayLabel: string;
  supportedBy: string[];
  sharingStatus: RosterSharingStatus;
  lastActiveLabel: string;
}

const CHILD_LAST_ACTIVE: Record<string, string> = {
  "child-3f1a": "Today",
  "child-9c2b": "Yesterday",
  "child-7e44": "14 Jun",
  "child-1d80": "Today",
  "child-5a6f": "13 Jun",
  "child-2b9e": "1 Jun",
  "child-4c7d": "Today",
};

export function buildRosterRows(): RosterRow[] {
  return children.map((child) => {
    const viewerLinks = childLinks.filter(
      (l) => l.childId === child.id && l.relation === "viewer",
    );
    const supportedBy = viewerLinks
      .map((l) => accounts.find((a) => a.id === l.accountId)?.email)
      .filter((e): e is string => !!e);
    const hasActive = viewerLinks.some((l) => {
      if (!l.consentId) return false;
      const c = consents.find((c2) => c2.id === l.consentId);
      return c && !c.revokedAt && l.scope.length > 0;
    });
    const hasRevoked = viewerLinks.some((l) => {
      if (!l.consentId) return false;
      const c = consents.find((c2) => c2.id === l.consentId);
      return c && !!c.revokedAt;
    });
    const sharingStatus: RosterSharingStatus = hasActive
      ? "active"
      : hasRevoked
        ? "revoked"
        : "pending";
    return {
      childId: child.id,
      displayLabel: child.displayLabel,
      supportedBy,
      sharingStatus,
      lastActiveLabel: CHILD_LAST_ACTIVE[child.id] ?? "—",
    };
  });
}

// ---- Consent log ------------------------------------------------------------

export type ConsentLogStatus = "active" | "pending" | "revoked";

export interface ConsentLogEntry {
  id: string;
  childLabel: string;
  memberEmail: string;
  scope: string[];
  status: ConsentLogStatus;
  grantedLabel: string;
  revokedLabel?: string;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

export function buildConsentLog(): ConsentLogEntry[] {
  return consents.map((consent) => {
    const link = childLinks.find((l) => l.consentId === consent.id);
    const child = link ? children.find((c) => c.id === link.childId) : null;
    const member = link ? accounts.find((a) => a.id === link.accountId) : null;
    const status: ConsentLogStatus = consent.revokedAt
      ? "revoked"
      : link && link.scope.length > 0
        ? "active"
        : "pending";
    return {
      id: consent.id,
      childLabel: child?.displayLabel ?? "—",
      memberEmail: member?.email ?? "—",
      scope: consent.scope as string[],
      status,
      grantedLabel: fmtDate(consent.grantedAt),
      revokedLabel: consent.revokedAt ? fmtDate(consent.revokedAt) : undefined,
    };
  });
}
